import {
  announceToScreenReader,
  getVuejsDialogMessage,
} from '@/utils/screenReaderAnnounce';

const FOCUSABLE_SELECTOR = [
  "a[href]",
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(", ");

function isVisible(element) {
  if (!element || !element.isConnected) {
    return false;
  }
  const style = window.getComputedStyle(element);
  if (style.display === "none" || style.visibility === "hidden") {
    return false;
  }
  // position:fixed overlays have offsetParent === null, so use the box size instead
  const rect = element.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
}

export function getFocusableElements(container) {
  return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
    (el) => !el.hasAttribute("disabled") && isVisible(el)
  );
}

function findDialogPanel() {
  const overlay = document.querySelector(".dg-container");
  if (!overlay || !isVisible(overlay)) {
    return null;
  }
  return overlay.querySelector(".dg-main-content") || overlay;
}

export function trapFocus(container, previousFocus) {
  if (!container) {
    return () => {};
  }

  container.setAttribute("aria-modal", "true");
  if (!container.getAttribute("role")) {
    container.setAttribute("role", "dialog");
  }
  if (!container.hasAttribute("tabindex")) {
    container.setAttribute("tabindex", "-1");
  }

  const focusModal = () => {
    if (typeof container.focus === "function") {
      container.focus();
    }
  };

  const handleKeyDown = (event) => {
    if (event.key !== "Tab") {
      return;
    }

    const focusable = getFocusableElements(container);
    if (!focusable.length) {
      event.preventDefault();
      focusModal();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    const inside = container.contains(active);

    if (event.shiftKey) {
      if (!inside || active === first || active === container) {
        event.preventDefault();
        last.focus();
      }
    } else if (!inside || active === last || active === container) {
      event.preventDefault();
      first.focus();
    }
  };

  const handleFocusIn = (event) => {
    if (container.contains(event.target)) {
      return;
    }
    const focusable = getFocusableElements(container);
    if (focusable.length) {
      focusable[0].focus();
      return;
    }
    focusModal();
  };

  document.addEventListener("keydown", handleKeyDown, true);
  document.addEventListener("focusin", handleFocusIn);
  requestAnimationFrame(() => {
    requestAnimationFrame(focusModal);
  });

  return () => {
    document.removeEventListener("keydown", handleKeyDown, true);
    document.removeEventListener("focusin", handleFocusIn);
    container.removeAttribute("aria-modal");
    if (container.getAttribute("role") === "dialog") {
      container.removeAttribute("role");
    }
    if (container.getAttribute("tabindex") === "-1") {
      container.removeAttribute("tabindex");
    }
    if (previousFocus && typeof previousFocus.focus === "function") {
      requestAnimationFrame(() => {
        previousFocus.focus();
      });
    }
  };
}

let activeRelease = null;
let trappedContainer = null;
let observer = null;
let scanScheduled = false;
let backgroundRoot = null;

function hideBackgroundFromAssistiveTech() {
  backgroundRoot = document.getElementById("app");
  if (backgroundRoot) {
    backgroundRoot.setAttribute("aria-hidden", "true");
    backgroundRoot.inert = true;
  }
}

function showBackgroundToAssistiveTech() {
  if (backgroundRoot) {
    backgroundRoot.removeAttribute("aria-hidden");
    backgroundRoot.inert = false;
    backgroundRoot = null;
  }
}

function releaseActiveTrap() {
  if (activeRelease) {
    activeRelease();
    activeRelease = null;
  }
  if (trappedContainer) {
    delete trappedContainer.dataset.focusTrapActive;
    trappedContainer.removeAttribute('aria-label');
    trappedContainer = null;
  }
  showBackgroundToAssistiveTech();
}

function activateTrap(container) {
  if (!container || container.dataset.focusTrapActive === "true") {
    return;
  }

  releaseActiveTrap();
  const previousFocus = document.activeElement;
  container.dataset.focusTrapActive = "true";
  trappedContainer = container;
  hideBackgroundFromAssistiveTech();

  const dialogMessage = getVuejsDialogMessage(container);
  if (dialogMessage) {
    container.setAttribute('aria-label', dialogMessage);
    announceToScreenReader(dialogMessage);
  }

  activeRelease = trapFocus(container, previousFocus);
}

function scanForDialog() {
  const panel = findDialogPanel();

  if (panel) {
    if (panel !== trappedContainer) {
      activateTrap(panel);
    }
    return;
  }

  releaseActiveTrap();
}

function scheduleDialogScan() {
  if (scanScheduled) {
    return;
  }
  scanScheduled = true;
  requestAnimationFrame(() => {
    scanScheduled = false;
    scanForDialog();
  });
}

export function initVuejsDialogFocusTrap() {
  if (typeof document === "undefined" || observer) {
    return;
  }

  observer = new MutationObserver(() => {
    scheduleDialogScan();
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["class", "style", "aria-hidden"],
  });

  scanForDialog();
}

export function destroyVuejsDialogFocusTrap() {
  if (observer) {
    observer.disconnect();
    observer = null;
  }
  releaseActiveTrap();
}



// WEBPACK FOOTER //
// ./src/utils/focusTrap.js